import { MembraneSrnfSchema as BackendSrnfMembraneSchema } from "../../../../../backend/models/membrane-srnf.model"
import { useQuery } from '@tanstack/react-query'
import { fetchMembranes, fetchMembranesSchema, Membrane, MembraneFilters, MembranePaths, MembraneSchema } from '../membranes-queries'
import { RoMembranePaths } from "../ro/ro-queries"
import { CommonPaths, ResourceSchema } from "../../omd"
import { cloneDeep } from "lodash"


export type SrnfMembrane = Membrane<BackendSrnfMembraneSchema>

export type SrnfMembranePaths = MembranePaths<SrnfMembrane>

export type SrnfMembraneAllowedPaths = CommonPaths<
  SrnfMembranePaths,
  'characterizationResults.characterizationData' |
  'characterizationResults.contactAngle' |
  'characterizationResults.totalThickness' |
  'filtrationResults.mwco' |
  'filtrationResults.permeance' |
  'filtrationResults.solventPermeance' |
  'modification' |
  'supportLayerChemistry' |
  'testConditions.filtrationMode' |
  'testConditions.hydraulicP' |
  'testConditions.solvent1' |
  'testConditions.solvent2' |
  'testConditions.temperature' |
  "type" |
  "chemistry" |
  "structure" |
  "report.publicationDate"
> | 'soluteChoice.solute.molecularWeight'

// All filters available to the user for SRNF Membranes
export const srnfMembranesAllowedPaths: SrnfMembraneAllowedPaths[] =
  [
    'characterizationResults.characterizationData',
    'characterizationResults.contactAngle',
    'characterizationResults.totalThickness',
    'filtrationResults.mwco',
    'filtrationResults.permeance',
    'filtrationResults.solventPermeance',
    'modification',
    'supportLayerChemistry',
    'testConditions.filtrationMode',
    'testConditions.hydraulicP',
    'testConditions.solvent1',
    'testConditions.solvent2',
    'testConditions.temperature',
    'type',
    "chemistry",
    "structure",
    'report.publicationDate',
    'soluteChoice.solute.molecularWeight',
  ] as const

export type SrnfMembranesFilters = MembraneFilters<SrnfMembrane, SrnfMembraneAllowedPaths> & { 'filter': string }

export type SrnfMembraneSchema = MembraneSchema<SrnfMembraneAllowedPaths | 'soluteChoice.solute.molecularWeight'>

export type SrnfMembranesQueryKey = 'srnf-membranes'
export const queryKey: SrnfMembranesQueryKey = 'srnf-membranes'
export const srnfMembranesQueryPath = '/membranes-srnf'
export type SrnfMembranesSchemaQueryKey = 'srnf-membranes-schema'
export const schemaQueryKey: SrnfMembranesSchemaQueryKey = 'srnf-membranes-schema'
export const srnfMembranesSchemaQueryPath = srnfMembranesQueryPath + '/schema'

export const fetchSrnfMembranes = (params?: SrnfMembranesFilters) => {
  let newParams = cloneDeep(params)
  if (newParams && 'soluteChoice.solute.molecularWeight' in newParams) {
    if (typeof newParams['soluteChoice.solute.molecularWeight'] === 'object') {
      if (newParams['soluteChoice.solute.molecularWeight']) {
        const min = 'min' in newParams['soluteChoice.solute.molecularWeight'] ? newParams['soluteChoice.solute.molecularWeight'].min : undefined
        const max = 'max' in newParams['soluteChoice.solute.molecularWeight'] ? newParams['soluteChoice.solute.molecularWeight'].max : undefined
        newParams['filter'] = JSON.stringify({
          $or: [
            { $and: [{ 'soluteChoice.solute1.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute2.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute3.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute4.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute5.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute6.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute7.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute8.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute9.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute10.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute11.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute12.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute13.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute14.molecularWeight': { $gte: min, $lte: max } }] },
            { $and: [{ 'soluteChoice.solute15.molecularWeight': { $gte: min, $lte: max } }] },
          ]
        })
      }
      console.log(newParams)
      delete newParams['soluteChoice.solute.molecularWeight']
    }
  }
  return fetchMembranes<SrnfMembrane, SrnfMembranesFilters>('/membranes-srnf', newParams)
}
export const fetchSrnfMembranesSchema = async () => {
  const srnfMembranesSchema = await fetchMembranesSchema<SrnfMembraneSchema>('/membranes-srnf/schema')
  srnfMembranesSchema['soluteChoice.solute.molecularWeight'] = {
    type: 'Number',
    path: 'soluteChoice.solute.molecularWeight',
    key: 'rejection',
    labels: { short: 'Solute molecular weight', long: 'Solute molecular weight' },
    validations: {}
  }
  return srnfMembranesSchema
}

export const useSrnfMembranesQuery = (params?: SrnfMembranesFilters) => useQuery(
  [queryKey, params],
  () => fetchSrnfMembranes(params),
)